import { useState, useEffect } from 'react'
import { authFetch } from '../../lib/api'
import { usePulseMemory } from '../PulseMemory/usePulseMemory'

interface PrivacyData {
  stats: { totalConversations: number; modelsUsed: number; projects: number }
  providerFiles: Record<string, string[]>
  recent: Array<{ model: string; mode: string; date: string; tokensIn: number; tokensOut: number; files: string[] }>
}

export function PrivacyPanel() {
  const [data, setData] = useState<PrivacyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const memory = usePulseMemory(s => s.memory)
  const clear = usePulseMemory(s => s.clear)

  const load = async () => {
    setLoading(true)
    try {
      const r = await authFetch('/api/privacy')
      if (r.ok) setData(await r.json())
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const handleExport = async () => {
    const r = await authFetch('/api/privacy/export')
    if (!r.ok) return
    const blob = await r.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'pulse-data-export.json'; a.click()
    URL.revokeObjectURL(url)
  }

  const handleDeleteTraining = async () => {
    if (!confirm('Delete ALL AI training data? This cannot be undone.')) return
    setDeleting(true)
    await authFetch('/api/privacy/training', { method: 'DELETE' })
    setDeleting(false)
    load()
  }

  const handleClearMemory = () => {
    if (!confirm('Clear all local AI memory (project, goals, patterns, notes)?')) return
    clear()
  }

  const s: React.CSSProperties = { padding: 20, height: '100%', overflow: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-1)' }
  const card: React.CSSProperties = { background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginBottom: 12 }
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' }
  const dangerBtn: React.CSSProperties = { ...btn, background: '#dc262620', border: '1px solid #dc2626', color: '#f87171' }

  if (loading) return <div style={s}>Loading privacy data...</div>

  return (
    <div style={s}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--accent)' }}>🛡️ Privacy Dashboard</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--text-2)', fontSize: 11 }}>See exactly what data AI has access to. GDPR-ready controls.</p>

      {/* Stats */}
      <div style={card}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>📊 Data Summary</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <div><span style={{ color: 'var(--text-2)' }}>Conversations</span><br/><strong>{data?.stats.totalConversations ?? 0}</strong></div>
          <div><span style={{ color: 'var(--text-2)' }}>Models Used</span><br/><strong>{data?.stats.modelsUsed ?? 0}</strong></div>
          <div><span style={{ color: 'var(--text-2)' }}>Projects</span><br/><strong>{data?.stats.projects ?? 0}</strong></div>
        </div>
      </div>

      {/* Local Memory */}
      <div style={card}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>🧠 Local AI Memory</div>
        <div style={{ fontSize: 11, color: 'var(--text-2)', lineHeight: 1.6 }}>
          <div><strong>Project:</strong> {memory.project || '—'}</div>
          <div><strong>Goal:</strong> {memory.goal || '—'}</div>
          <div><strong>Stack:</strong> {memory.stack.length ? memory.stack.join(', ') : '—'}</div>
          <div><strong>Patterns:</strong> {memory.patterns.length} learned</div>
          <div><strong>Self-notes:</strong> {memory.selfNotes.length} entries</div>
          <div><strong>Recent files:</strong> {memory.recentFiles.length} tracked</div>
        </div>
        <button style={{ ...dangerBtn, marginTop: 10 }} onClick={handleClearMemory}>Clear Local Memory</button>
      </div>

      {/* Files sent to providers */}
      <div style={card}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>📁 Files Sent to AI Providers</div>
        {data && Object.keys(data.providerFiles).length > 0 ? (
          Object.entries(data.providerFiles).map(([model, files]) => (
            <div key={model} style={{ marginBottom: 10 }}>
              <div style={{ color: 'var(--accent)', fontSize: 11, marginBottom: 4 }}>{model} ({files.length} files)</div>
              <div style={{ maxHeight: 80, overflow: 'auto', fontSize: 10, color: 'var(--text-2)' }}>
                {files.slice(0, 20).map(f => <div key={f}>{f}</div>)}
                {files.length > 20 && <div>...and {files.length - 20} more</div>}
              </div>
            </div>
          ))
        ) : <div style={{ color: 'var(--text-2)' }}>No files sent yet.</div>}
      </div>

      {/* Recent Activity */}
      <div style={card}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>🕐 Recent AI Activity</div>
        <div style={{ maxHeight: 150, overflow: 'auto' }}>
          {data?.recent.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: '1px solid var(--border)', fontSize: 10 }}>
              <span style={{ color: 'var(--accent)', minWidth: 80 }}>{r.model}</span>
              <span style={{ color: 'var(--text-2)', minWidth: 30 }}>{r.mode}</span>
              <span style={{ color: 'var(--text-2)' }}>{r.tokensIn + r.tokensOut} tok</span>
              <span style={{ color: 'var(--text-2)', marginLeft: 'auto' }}>{new Date(r.date).toLocaleDateString()}</span>
            </div>
          ))}
          {(!data?.recent.length) && <div style={{ color: 'var(--text-2)' }}>No activity yet.</div>}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button style={btn} onClick={handleExport}>📥 Export All Data</button>
        <button style={dangerBtn} onClick={handleDeleteTraining} disabled={deleting}>
          {deleting ? 'Deleting...' : '🗑️ Delete Training Data'}
        </button>
      </div>
    </div>
  )
}
